import classNames from "classnames";
import { HTMLAttributes, useEffect, useState } from "react";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faClose } from "@fortawesome/free-solid-svg-icons";

import Alert, { AlertProps } from "src/components-v2/Alert";
import { FlashWrapper } from "./Flash.styled";
import Icon from "../Icon";

const IN_BROWSER = typeof window !== "undefined";

export type FlashProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * Flag to show the flash
   */
  show: boolean;

  /**
   * Props for different type of colors
   * @type {"blue" | "green" | "grey" | "red" | "yellow"}
   * @default {"red"}
   */
  color: AlertProps["color"];

  /**
   * icon prop for fontaweosome icon
   */
  icon?: IconProp;

  /**
   * String of emoticon
   */
  emoticon?: string;

  /**
   * Callback when closing
   */
  onClose?: () => void;

  /**
   * Showing the close button
   */
  withCloseButton?: boolean;
};

function Flash(props: FlashProps) {
  const { children, show, color, icon, emoticon, onClose = () => {}, withCloseButton, ...restProps } = props;

  const UA = IN_BROWSER && window.navigator.userAgent.toLowerCase();
  const IS_IE_9 = UA && UA.indexOf("msie 9.0") >= 0;

  const [isShow, setIsShow] = useState(false);
  const [animationType, setAnimationType] = useState("leave");

  useEffect(() => {
    if (show) enter();
    else leave();
  }, [show]);

  function enter() {
    setTimeout(() => {
      onClose();
    }, 3000);
    setIsShow(true);
    setAnimationType("enter");
  }

  function leave() {
    if (IS_IE_9) {
      setIsShow(false);
    } else {
      setAnimationType("leave");
    }
  }

  function animationEnd() {
    if (animationType === "leave") {
      setIsShow(false);
    }
  }

  const flashClass = classNames("c-flash", `slide-${animationType}`);
  const flashStyle = { display: isShow ? "" : "none" };

  return (
    <FlashWrapper
      data-testid="qa-flash"
      style={flashStyle}
      className={flashClass}
      onAnimationEnd={animationEnd}
      {...restProps}
    >
      <Alert color={color} icon={icon} emoticon={emoticon} bordered>
        {children}
        {withCloseButton && (
          <span className="u-inline-block u-ml-3 u-cursor-pointer u-float-right" onClick={leave}>
            <Icon icon={faClose} />
          </span>
        )}
      </Alert>
    </FlashWrapper>
  );
}

export default Flash;
